import { Component, OnInit } from '@angular/core';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { DecimalPipe } from 'app/shared/decimal.pipe';
import { UIModalBaseComponent } from 'app/ui/ui-modal-base.component';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
	selector: 'modal-manage-sku-product-mapping',
	templateUrl: './modal-manage-sku-product-mapping.component.html'
})
export class ModalManageSKUProductMappingComponent extends UIModalBaseComponent implements OnInit {

	sku = '';
	skuId = null;
	productId = null;
	details = null;
	step = 'choose';

	// Rename
	newSku = '';
	skuChecking = false;
	skuAvailable = null;

	// Merge
	mergeSearch = '';
	mergeResults: any[] = [];
	mergeSearching = false;
	selectedTarget: any = null;
	sourceStats: any = null;
	targetStats: any = null;
	loadingSourceStats = false;
	loadingTargetStats = false;

	// Separate Allocated Stock
	separationInfo: any = null;
	loadingSeparationInfo = false;
	disabled = false;

	private skuCheckSubject = new Subject<string>();
	private mergeSearchSubject = new Subject<string>();

	constructor(
		public app: AppService,
		private api: ApiService
	) {
		super();
	}

	ngOnInit(): void {
		this.skuCheckSubject.pipe(
			debounceTime(400),
			switchMap(sku => {
				const trimmed = (sku || '').trim();
				if (!trimmed || trimmed === this.sku) {
					this.skuChecking = false;
					this.skuAvailable = null;
					return Promise.resolve(null);
				}
				this.skuChecking = true;
				return this.runSkuCheck(trimmed);
			})
		).subscribe(data => {
			this.skuChecking = false;
			if (data) this.skuAvailable = !!data.available;
		});

		this.mergeSearchSubject.pipe(
			debounceTime(400),
			switchMap(keywords => {
				const trimmed = (keywords || '').trim();
				if (!trimmed) {
					this.mergeSearching = false;
					this.mergeResults = [];
					return Promise.resolve(null);
				}
				this.mergeSearching = true;
				return this.runMergeSearch(trimmed);
			})
		).subscribe(data => {
			this.mergeSearching = false;
			if (data) this.mergeResults = data.list || [];
		});
	}

	open(mdata: any) {
		if(!this.app.orgInfo?.role?.access_warehouse) {
			this.app.notifications.showDanger("Access Denied. You don't have the 'Access Inventory' permission.");
			return;
		}
		this.sku = mdata?.sku || '';
		this.skuId = mdata?.sku_id || null;
		this.productId = mdata?.product_id || null;
		this.details = null;
		this.step = 'choose';
		this.resetRenameState();
		this.resetMergeState();

		if (this.productId) {
			this.disabled = true;
			this.api.inventory.getResolveProductInfo({ product_id: this.productId }, data => {
				this.disabled = false;
				this.details = data;
				if(!this.modal.isOpen) this.modal.open();
			}, error => {
				this.disabled = false;
				this.app.notifications.showDanger(error.message);
			});
		} else {
			if(!this.modal.isOpen) this.modal.open();
		}
	}

	choose(action: 'rename' | 'merge' | 'separate') {
		if (action === 'rename') {
			this.step = 'rename';
			this.resetRenameState();
			return;
		}
		if (action === 'merge') {
			this.step = 'merge';
			this.resetMergeState();
			this.loadSourceStats();
			return;
		}
		if (action === 'separate') {
			this.step = 'separate';
			this.resetRenameState();
			this.resetSeparateState();
			this.loadSeparationInfo();
			return;
		}
		this.modal.close({ action });
	}

	back() {
		this.step = 'choose';
		this.resetRenameState();
		this.resetMergeState();
		this.resetSeparateState();
	}

	// ---------- Rename ----------

	onNewSkuInput() {
		this.skuAvailable = null;
		const trimmed = (this.newSku || '').trim();
		this.skuChecking = !!trimmed && trimmed !== this.sku;
		this.skuCheckSubject.next(this.newSku);
	}

	rename() {
		const sku = (this.newSku || '').trim();
		if (!this.skuId || !sku || sku === this.sku || this.skuAvailable !== true || this.skuChecking || this.disabled) return;

		const rows = [
			this.summaryRow('Current SKU', this.sku),
			this.summaryRow('New SKU', sku)
		].join('');

		const content = `<div class="mb-3">Rename this Warehouse SKU across all warehouses and sites. Stock, history, and assignments stay intact.</div>${rows}`;

		this.app.dialog.popHtml('Rename Warehouse SKU?', content, ['Cancel', '*Rename'], btn => {
			if (btn === 1) this.doRename(sku);
		});
	}

	doRename(sku: string) {
		this.disabled = true;
		this.api.inventory.renameWarehouseSku({ sku_id: this.skuId, sku }, () => {
			this.disabled = false;
			this.app.notifications.showSuccess('Warehouse SKU renamed successfully');
			this.modal.close({ action: 'rename', sku_id: this.skuId, new_sku: sku });
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	// ---------- Merge ----------

	onMergeSearchInput() {
		const trimmed = (this.mergeSearch || '').trim();
		if (!trimmed) {
			this.mergeSearching = false;
			this.mergeResults = [];
		} else {
			this.mergeSearching = true;
		}
		this.mergeSearchSubject.next(this.mergeSearch);
	}

	selectTarget(target: any) {
		this.selectedTarget = target;
		this.targetStats = null;
		if (target?.sku_id) {
			this.loadingTargetStats = true;
			this.api.inventory.getWarehouseSkuStats({ sku_id: target.sku_id }, data => {
				this.loadingTargetStats = false;
				if (this.selectedTarget?.sku_id === target.sku_id) this.targetStats = data;
			}, error => {
				this.loadingTargetStats = false;
				this.app.notifications.showDanger(error.message);
			});
		}
	}

	clearTarget() {
		this.selectedTarget = null;
		this.targetStats = null;
		this.mergeSearch = '';
		this.mergeResults = [];
		this.mergeSearching = false;
	}

	merge() {
		if (!this.skuId || !this.selectedTarget?.sku_id || this.disabled) return;

		const rows = [
			this.summaryRow('Source SKU', this.sku),
			this.summaryRow('Target SKU', this.selectedTarget.warehouse_sku)
		].join('');

		const content = `<div class="mb-3">All stock, POs, reservations, history and product mappings under <b>${this.escapeHtml(this.sku)}</b> will move under <b>${this.escapeHtml(this.selectedTarget.warehouse_sku)}</b> across all warehouses and sites. The source SKU code will be permanently deleted.</div>${rows}`;

		this.app.dialog.popHtml('Merge Warehouse SKUs?', content, ['Cancel', '!Merge'], btn => {
			if (btn === 1) this.doMerge();
		});
	}

	doMerge() {
		this.disabled = true;
		this.api.inventory.mergeWarehouseSkus({ source_sku_id: this.skuId, target_sku_id: this.selectedTarget.sku_id }, () => {
			this.disabled = false;
			this.app.notifications.showSuccess('Warehouse SKUs merged successfully');
			this.modal.close({
				action: 'merge',
				source_sku_id: this.skuId,
				target_sku_id: this.selectedTarget.sku_id,
				new_sku: this.selectedTarget.warehouse_sku
			});
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	loadSourceStats() {
		if (!this.skuId) return;
		this.loadingSourceStats = true;
		this.api.inventory.getWarehouseSkuStats({ sku_id: this.skuId }, data => {
			this.loadingSourceStats = false;
			this.sourceStats = data;
		}, error => {
			this.loadingSourceStats = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	runMergeSearch(keywords: string) {
		return new Promise<any>(resolve => {
			this.api.inventory.searchWarehouseSkus(
				{ keywords, product_id: null, exclude_sku_id: this.skuId },
				data => resolve(data),
				() => resolve(null)
			);
		});
	}

	// ---------- Separate Allocated Stock ----------

	loadSeparationInfo() {
		if (!this.skuId || !this.productId) return;
		this.loadingSeparationInfo = true;
		this.api.inventory.getWarehouseSkuSeparationInfo({ sku_id: this.skuId, product_id: this.productId }, data => {
			this.loadingSeparationInfo = false;
			(data.warehouses || []).forEach(wh => {
				(wh.locations || []).forEach(loc => { loc.move_quantity = null; });
			});
			this.separationInfo = data;
		}, error => {
			this.loadingSeparationInfo = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	warehouseTypeLabel(type: string) {
		switch (type) {
			case 'warehouse': return 'Warehouse';
			case 'site': return 'Site';
			case 'van': return 'Van';
			case 'project_site': return 'Project Site';
			case 'supplier': return 'Supplier';
			default: return '';
		}
	}

	pickLimit(wh, loc) {
		// Cap at the location's quantity less its units locked in open transfers,
		// and at the warehouse's free stock less the other locations' picks
		if (loc.is_staging) return 0;
		let others = 0;
		(wh.locations || []).forEach(l => {
			if (l !== loc) others += DecimalPipe.parse(l.move_quantity || 0);
		});
		const free = DecimalPipe.parse(wh.free_stock || 0);
		const locFree = DecimalPipe.parse(loc.quantity || 0) - DecimalPipe.parse(loc.in_transfer_qty || 0);
		return Math.max(0, Math.min(locFree, free - others));
	}

	incrementPick(wh, loc) {
		this.setPick(wh, loc, DecimalPipe.parse(loc.move_quantity || 0) + 1);
	}

	decrementPick(wh, loc) {
		this.setPick(wh, loc, DecimalPipe.parse(loc.move_quantity || 0) - 1);
	}

	onPickChange(wh, loc) {
		this.setPick(wh, loc, DecimalPipe.parse(loc.move_quantity || 0));
	}

	pickAllFree(wh) {
		// UI-only: distribute the warehouse's free stock across its locations, skipping
		// staging locations and units locked in open transfers.
		let remaining = DecimalPipe.parse(wh.free_stock || 0);
		(wh.locations || []).forEach(loc => {
			if (loc.is_staging) {
				loc.move_quantity = null;
				return;
			}
			const locFree = Math.max(0, DecimalPipe.parse(loc.quantity || 0) - DecimalPipe.parse(loc.in_transfer_qty || 0));
			const take = Math.min(locFree, remaining);
			loc.move_quantity = take > 0 ? DecimalPipe.transform(take, 0, 4, false) : null;
			remaining -= take;
		});
	}

	pickQty(loc) {
		return DecimalPipe.parse(loc.move_quantity || 0);
	}

	separateAllocatedTotal() {
		// Sum the allocations list, not the picker's warehouse list — the latter is filtered
		// to free-stock warehouses and would miss allocations held at allocation-only locations
		if (!this.separationInfo) return 0;
		return (this.separationInfo.allocations || []).reduce((sum, a) => sum + DecimalPipe.parse(a.quantity || 0), 0);
	}

	separateFreeTotal() {
		// Picks are free stock only — the allocated quantity moves separately
		if (!this.separationInfo) return 0;
		let total = 0;
		(this.separationInfo.warehouses || []).forEach(wh => {
			(wh.locations || []).forEach(loc => { total += DecimalPipe.parse(loc.move_quantity || 0); });
		});
		return total;
	}

	private setPick(wh, loc, qty: number) {
		if (isNaN(qty) || qty < 0) qty = 0;
		qty = Math.min(qty, this.pickLimit(wh, loc));
		// Format without thousand separators — a "1,000" string would break parsing and reach the API as a string
		loc.move_quantity = qty ? DecimalPipe.transform(qty, 0, 4, false) : null;
	}

	separate() {
		const sku = (this.newSku || '').trim();
		if (!this.skuId || !this.productId || !sku || sku === this.sku || this.skuAvailable !== true || this.skuChecking || this.disabled || !this.separationInfo) return;

		const allocations = this.separationInfo.allocations || [];
		const rows = [
			this.summaryRow('New SKU', sku),
			this.summaryRow('Product', this.details?.short_description || ''),
			this.summaryRow('Allocations moving', allocations.length),
			this.summaryRow('Allocated stock moving', this.separateAllocatedTotal()),
			this.summaryRow('Free stock moving', this.separateFreeTotal())
		].join('');

		const content = `<div class="mb-3">The product, its allocations and the stock backing them will move to the new Warehouse SKU. <b>${this.escapeHtml(this.sku)}</b> keeps its history and any other mapped products.</div>${rows}`;

		this.app.dialog.popHtml('Separate Allocated Stock?', content, ['Cancel', '!Separate'], btn => {
			if (btn === 1) this.doSeparate(sku);
		});
	}

	doSeparate(sku: string) {
		const free_stock = [];
		(this.separationInfo.warehouses || []).forEach(wh => {
			(wh.locations || []).forEach(loc => {
				const qty = DecimalPipe.parse(loc.move_quantity || 0);
				if (qty > 0) free_stock.push({ warehouse_id: wh.warehouse_id, location_id: loc.location_id, quantity: qty });
			});
		});

		this.disabled = true;
		this.api.inventory.separateWarehouseSkuStock({
			sku_id: this.skuId,
			product_id: this.productId,
			new_sku: sku,
			free_stock
		}, data => {
			this.disabled = false;
			this.app.notifications.showSuccess('Allocated stock separated onto new Warehouse SKU successfully');
			this.modal.close({ action: 'separate', new_sku_id: data.sku_id, new_sku: data.sku });
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	// ---------- Shared ----------

	resetRenameState() {
		this.newSku = '';
		this.skuChecking = false;
		this.skuAvailable = null;
		this.disabled = false;
	}

	resetMergeState() {
		this.mergeSearch = '';
		this.mergeResults = [];
		this.mergeSearching = false;
		this.selectedTarget = null;
		this.sourceStats = null;
		this.targetStats = null;
		this.loadingSourceStats = false;
		this.loadingTargetStats = false;
		this.disabled = false;
	}

	resetSeparateState() {
		this.separationInfo = null;
		this.loadingSeparationInfo = false;
		this.disabled = false;
	}

	runSkuCheck(sku: string) {
		return new Promise<any>(resolve => {
			this.api.inventory.checkWarehouseSku({ sku, sku_id: this.skuId }, data => resolve(data), () => resolve(null));
		});
	}

	summaryRow(label: string, value: any): string {
		return `<div class="d-flex justify-content-between py-1 border-top"><span class="text-secondary mr-3">${this.escapeHtml(label)}</span><span class="text-bold text-right">${this.escapeHtml(value)}</span></div>`;
	}

	escapeHtml(value: any): string {
		return ('' + (value ?? '')).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
	}

}
